/**
 * M3 Button Component
 * Material Design 3 compliant button with variants and state layers
 *
 * Variants:
 *   - filled: High emphasis, primary actions (default)
 *   - filledTonal: Medium-high emphasis, secondary actions
 *   - outlined: Medium emphasis, secondary actions
 *   - text: Low emphasis, tertiary actions
 *   - elevated: Medium emphasis with shadow
 *
 * Usage:
 *   <Button>Primary Action</Button>
 *   <Button variant="outlined">Cancel</Button>
 *   <Button variant="filledTonal" icon={<Icon name="play" />}>Start Practice</Button>
 */

import React from 'react';

type ButtonVariant = 'filled' | 'filledTonal' | 'outlined' | 'text' | 'elevated';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonColor = 'primary' | 'secondary' | 'error' | 'success';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant (default: filled) */
  variant?: ButtonVariant;
  /** Button size (default: md) */
  size?: ButtonSize;
  /** Color scheme (default: primary) */
  color?: ButtonColor;
  /** Icon to display before text */
  icon?: React.ReactNode;
  /** Icon to display after text */
  iconEnd?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Children */
  children: React.ReactNode;
}

// Size mappings
const SIZE_MAP = {
  sm: {
    padding: 'px-3 py-1.5',
    text: 'text-sm',
    iconGap: 'gap-1',
    minHeight: 'min-h-[32px]',
    rounded: 'rounded-lg',
  },
  md: {
    padding: 'px-6 py-2.5',
    text: 'text-base',
    iconGap: 'gap-2',
    minHeight: 'min-h-[40px]',
    rounded: 'rounded-xl',
  },
  lg: {
    padding: 'px-8 py-3.5',
    text: 'text-lg',
    iconGap: 'gap-2',
    minHeight: 'min-h-[48px]',
    rounded: 'rounded-2xl',
  },
};

// Get variant-specific classes
function getVariantClasses(variant: ButtonVariant, color: ButtonColor): string {
  // M3 semantic color mapping
  const colorClasses = {
    primary: {
      solid: 'primary',
      onSolid: 'on-primary',
    },
    secondary: {
      solid: 'secondary',
      onSolid: 'on-secondary',
    },
    error: {
      solid: 'error',
      onSolid: 'on-error',
    },
    success: {
      solid: 'success',
      onSolid: 'on-success',
    },
  };

  const c = colorClasses[color];

  switch (variant) {
    case 'filled':
      return `bg-${c.solid} text-${c.onSolid} hover:brightness-110 before:bg-${c.onSolid}`;

    case 'filledTonal':
      return `bg-${c.solid}/20 text-${c.solid} hover:bg-${c.solid}/30 before:bg-${c.solid}`;

    case 'outlined':
      return `bg-transparent text-${c.solid} border-2 border-${c.solid}/50 hover:border-${c.solid} hover:bg-${c.solid}/10 before:bg-${c.solid}`;

    case 'text':
      return `bg-transparent text-${c.solid} hover:bg-${c.solid}/10 before:bg-${c.solid}`;

    case 'elevated':
      return `bg-surface-container text-${c.solid} shadow-elevation-1 hover:shadow-elevation-2 hover:bg-surface-container-high before:bg-${c.solid}`;

    default:
      return '';
  }
}

// Get focus ring color - using M3 semantic tokens
function getFocusRingClass(color: ButtonColor): string {
  const rings = {
    primary: 'focus-visible:ring-primary/50',
    secondary: 'focus-visible:ring-secondary/50',
    error: 'focus-visible:ring-error/50',
    success: 'focus-visible:ring-success/50',
  };
  return rings[color];
}

export function Button({
  variant = 'filled',
  size = 'md',
  color = 'primary',
  icon,
  iconEnd,
  fullWidth = false,
  loading = false,
  disabled,
  className = '',
  children,
  ...props
}: ButtonProps): JSX.Element {
  const sizeStyles = SIZE_MAP[size];
  const variantClasses = getVariantClasses(variant, color);
  const focusRing = getFocusRingClass(color);

  // Build class string
  const baseClasses = [
    // Base styles
    'relative',
    'inline-flex',
    'items-center',
    'justify-center',
    'font-semibold',
    'transition-all',
    'duration-200',
    'select-none',
    'overflow-hidden',

    // Size
    sizeStyles.padding,
    sizeStyles.text,
    sizeStyles.iconGap,
    sizeStyles.minHeight,
    sizeStyles.rounded,

    // Variant-specific
    variantClasses,

    // State layer (pseudo-element for M3 state overlay)
    'before:absolute',
    'before:inset-0',
    'before:opacity-0',
    'before:transition-opacity',
    'before:duration-200',
    'before:pointer-events-none',

    // Hover state layer (M3: 8% opacity)
    'hover:before:opacity-[0.08]',

    // Focus state (M3: 10% opacity)
    'focus:outline-hidden',
    'focus:before:opacity-[0.10]',
    'focus-visible:ring-2',
    'focus-visible:ring-offset-2',
    'focus-visible:ring-offset-dark-bg',
    focusRing,

    // Active/pressed state (M3: 10% opacity)
    'active:before:opacity-[0.10]',
    'active:scale-[0.98]',

    // Disabled state
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'disabled:hover:before:opacity-0',
    'disabled:active:scale-100',

    // Full width
    fullWidth ? 'w-full' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button disabled={disabled || loading} className={`${baseClasses} ${className}`} {...props}>
      {/* Content wrapper (above state layer) */}
      <span className="relative z-10 inline-flex items-center justify-center gap-2">
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {children}
          </span>
        ) : (
          <>
            {icon && <span className="shrink-0 inline-flex items-center">{icon}</span>}
            {children}
            {iconEnd && <span className="shrink-0 inline-flex items-center">{iconEnd}</span>}
          </>
        )}
      </span>
    </button>
  );
}

/**
 * IconButton - M3 icon-only button
 */
interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'standard' | 'filled' | 'filledTonal' | 'outlined';
  /** Size */
  size?: 'sm' | 'md' | 'lg';
  /** Color */
  color?: ButtonColor;
  /** Icon element */
  children: React.ReactNode;
  /** Accessibility label */
  'aria-label': string;
}

const ICON_BUTTON_SIZE = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
};

export function IconButton({
  variant = 'standard',
  size = 'md',
  color = 'primary',
  className = '',
  children,
  ...props
}: IconButtonProps): JSX.Element {
  // Map standard to text for reusing variant logic
  const effectiveVariant = variant === 'standard' ? 'text' : variant;
  const variantClasses = getVariantClasses(effectiveVariant as ButtonVariant, color);
  const focusRing = getFocusRingClass(color);

  const baseClasses = [
    'relative',
    'inline-flex',
    'items-center',
    'justify-center',
    'rounded-full',
    'transition-all',
    'duration-200',
    'overflow-hidden',

    // Size
    ICON_BUTTON_SIZE[size],

    // Variant
    variantClasses,

    // State layer
    'before:absolute',
    'before:inset-0',
    'before:rounded-full',
    'before:opacity-0',
    'before:transition-opacity',
    'before:pointer-events-none',

    // States
    'hover:before:opacity-[0.08]',
    'focus:outline-hidden',
    'focus:before:opacity-[0.10]',
    'focus-visible:ring-2',
    focusRing,
    'active:before:opacity-[0.10]',
    'active:scale-95',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={`${baseClasses} ${className}`} {...props}>
      <span className="relative z-10">{children}</span>
    </button>
  );
}
